/** Identifier for each standard number set, ordered from innermost to outermost. */
export type NumberSetId = 'N' | 'Z' | 'Q' | 'R';

/** Configuration for a single number set in the Venn diagram. */
export interface NumberSetConfig {
  /** Unique identifier for this set */
  id: NumberSetId;
  /** The set symbol displayed as a label (e.g. "ℕ", "ℤ") */
  symbol: string;
  /** Human-readable name (e.g. "Natural Numbers") */
  name: string;
  /** Accent color hex string (used for highlight fill, stroke, label, and examples) */
  accentColor: string;
  /** Example elements shown inside this set region (LaTeX strings rendered via KaTeX) */
  examples: string[];
}

/** Top-level input params for the component. */
export interface NumberSetsVennParams {
  /**
   * Ordered list of sets to display, from innermost to outermost.
   * Each set is assumed to be a subset of the next.
   * E.g. [N-config, Z-config, Q-config, R-config] for ℕ ⊂ ℤ ⊂ ℚ ⊂ ℝ
   */
  sets: NumberSetConfig[];
  /**
   * Force-highlight a specific set (and all its subsets).
   * null/undefined = default hover-only mode.
   * When set, the diagram behaves as if the user is hovering over that set.
   */
  highlightedSet?: NumberSetId | null;
  /** Whether hover interaction is enabled. Default: true */
  hoverEnabled?: boolean;
  /** Whether to show example values on hover. Default: true */
  showExamples?: boolean;
}

// ── Defaults ────────────────────────────────────────────────

export const DEFAULT_NUMBER_SETS: NumberSetConfig[] = [
  {
    id: 'N',
    symbol: 'ℕ',
    name: 'Natural Numbers',
    accentColor: '#22c55e',
    examples: ['0, 1, 2, \\ldots'],
  },
  {
    id: 'Z',
    symbol: 'ℤ',
    name: 'Integers',
    accentColor: '#a855f7',
    examples: ['-1, -2, -3, \\ldots'],
  },
  {
    id: 'Q',
    symbol: 'ℚ',
    name: 'Rational Numbers',
    accentColor: '#ef4444',
    examples: ['\\tfrac{1}{2}, \\tfrac{3}{4}, -\\tfrac{1}{3}, \\ldots'],
  },
  {
    id: 'R',
    symbol: 'ℝ',
    name: 'Real Numbers',
    accentColor: '#f97316',
    examples: ['\\sqrt{2}, \\pi, e, \\ldots'],
  },
];

export const DEFAULT_NUMBER_SETS_VENN_PARAMS: NumberSetsVennParams = {
  sets: DEFAULT_NUMBER_SETS,
  highlightedSet: null,
  hoverEnabled: true,
  showExamples: true,
};
